import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api, { hrPipelineApi } from '../lib/api';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import Pagination from '../components/common/Pagination';
import { DatePicker } from '../components/ui/date-picker';
import {
  Trophy, Search, Building2, Calendar, Loader2, User,
  Mail, Phone, Briefcase, Filter, ChevronDown, ChevronUp,
  IndianRupee, Pencil, CheckCircle, AlertTriangle, Receipt, PlusCircle
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Money helpers. Every figure on this page comes from the `placements`
// collection (Phase 5, routes/placements.py) via routes/placements_v2.py -
// nothing here estimates a fee. `ctc_confirmed: false` rows were recorded
// from the candidate's on-file CTC when nobody typed the signed figure; they
// are shown as PROVISIONAL until someone edits them.
// ---------------------------------------------------------------------------
const formatINR = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '-';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const formatLakh = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '-';
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(n >= 10000000 ? 0 : 1)}L`;
  return formatINR(n);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const INVOICE_STYLES = {
  paid: 'bg-green-100 text-green-700 border-green-200',
  overdue: 'bg-red-100 text-red-700 border-red-200',
  due_soon: 'bg-amber-100 text-amber-700 border-amber-200',
  not_due: 'bg-slate-100 text-slate-600 border-slate-200',
  no_joining_date: 'bg-slate-100 text-slate-500 border-slate-200',
};

const InvoiceBadge = ({ p }) => {
  const st = p?.invoice_status || 'not_due';
  const label = st === 'paid' ? `Paid${p.invoice_paid_at ? ' ' + String(p.invoice_paid_at).slice(0, 10) : ''}`
    : st === 'overdue' ? `Invoice overdue ${p.days_overdue}d`
    : st === 'due_soon' ? (p.days_to_due === 0 ? 'Invoice due today' : `Invoice due in ${p.days_to_due}d`)
    : st === 'not_due' ? `Due ${p.invoice_due_date || ''}`
    : 'No joining date';
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${INVOICE_STYLES[st] || INVOICE_STYLES.not_due}`}>
      {label}
    </span>
  );
};

const CtcBadge = ({ p }) => (
  p?.ctc_confirmed
    ? <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Signed CTC</Badge>
    : <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />Provisional CTC</Badge>
);

// One line of money under a hired candidate card, read straight off the placement row.
const PlacementLine = ({ p, onEdit, onRecord }) => {
  if (!p) {
    return (
      <div className="mt-3 pt-3 border-t flex items-center justify-between gap-3 flex-wrap text-sm">
        <span className="text-slate-500 flex items-center gap-1">
          <IndianRupee className="w-4 h-4 text-slate-400" />
          No fee recorded for this hire
        </span>
        <Button size="sm" variant="outline" onClick={onRecord} data-testid="record-placement-btn">
          <PlusCircle className="w-4 h-4 mr-1" /> Record signed CTC
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-3 pt-3 border-t flex items-center justify-between gap-3 flex-wrap text-sm">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-1 text-slate-700">
          <IndianRupee className="w-4 h-4 text-emerald-500" />
          CTC <strong>{formatINR(p.signed_annual_ctc)}</strong>
        </span>
        <span className="text-slate-700">
          Fee <strong className="text-emerald-700">{formatINR(p.fee_amount)}</strong>
          {p.fee_percentage ? <span className="text-slate-500"> ({p.fee_percentage}%)</span> : null}
        </span>
        <span className="text-xs text-slate-500" title="Which rule produced the fee">{p.fee_basis}</span>
        <CtcBadge p={p} />
        <InvoiceBadge p={p} />
        {p.joining_date && (
          <span className="text-xs text-slate-500">Joined {String(p.joining_date).slice(0, 10)}</span>
        )}
      </div>
      <Button size="sm" variant="ghost" onClick={onEdit} data-testid="edit-placement-btn">
        <Pencil className="w-4 h-4 mr-1" /> Edit
      </Button>
    </div>
  );
};

export default function HiredCandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hiredCandidates, setHiredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [money, setMoney] = useState(null);            // /api/placements/v2/summary
  const [placementsByCandidate, setPlacementsByCandidate] = useState({});
  const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState({ companies: [] });
  const itemsPerPage = 20;

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    company: searchParams.get('company') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || ''
  });

  // Expanded details
  const [expandedId, setExpandedId] = useState(null);

  // Placements ledger (every row in `placements`, whichever path recorded it)
  const [ledger, setLedger] = useState([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerTotals, setLedgerTotals] = useState({ fee: 0, ctc: 0 });
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerUnpaid, setLedgerUnpaid] = useState(false);
  const [ledgerUnconfirmed, setLedgerUnconfirmed] = useState(false);
  const ledgerPerPage = 25;

  // Edit / record modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // placement row being edited, or null when recording a new one
  const [recordFor, setRecordFor] = useState(null);  // hired candidate row when recording a new placement
  const [form, setForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (filters.search) params.set('search', filters.search);
    if (filters.company) params.set('company', filters.company);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    setSearchParams(params, { replace: true });
  }, [page, filters, setSearchParams]);

  useEffect(() => {
    loadHiredCandidates();
    loadStats();
  }, [page]);

  useEffect(() => {
    loadLedger(ledgerPage);
  }, [ledgerPage, ledgerUnpaid, ledgerUnconfirmed]);

  const loadPlacementsFor = useCallback(async (rows) => {
    const ids = [...new Set((rows || []).map(r => r.candidate_id).filter(Boolean))];
    if (ids.length === 0) { setPlacementsByCandidate({}); return; }
    try {
      const res = await api.get('/placements/v2/list', { params: { candidate_ids: ids.join(','), limit: 500 } });
      const map = {};
      (res.data.placements || []).forEach(p => {
        // newest joining date wins when a candidate has more than one row
        if (!map[p.candidate_id] || String(p.joining_date || '') > String(map[p.candidate_id].joining_date || '')) {
          map[p.candidate_id] = p;
        }
      });
      setPlacementsByCandidate(map);
    } catch (err) {
      // module not installed yet - cards simply show no money line
      setPlacementsByCandidate({});
    }
  }, []);

  const loadHiredCandidates = async (filterParams = filters) => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
        ...filterParams
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const res = await hrPipelineApi.getHiredCandidates(params);
      const rows = res.data.hired_candidates || [];
      setHiredCandidates(rows);
      setTotal(res.data.total || 0);
      setFilterOptions(res.data.filter_options || { companies: [] });
      loadPlacementsFor(rows);
    } catch (err) {
      toast.error('Failed to load hired candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await hrPipelineApi.getHiredStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
    try {
      const res = await api.get('/placements/v2/summary');
      setMoney(res.data);
    } catch (err) {
      setMoney(null);
    }
  };

  const loadLedger = async (pageNum = ledgerPage) => {
    setLedgerLoading(true);
    try {
      const params = { skip: (pageNum - 1) * ledgerPerPage, limit: ledgerPerPage };
      if (ledgerSearch) params.search = ledgerSearch;
      if (ledgerUnpaid) params.unpaid_only = true;
      if (ledgerUnconfirmed) params.unconfirmed_only = true;
      const res = await api.get('/placements/v2/list', { params });
      setLedger(res.data.placements || []);
      setLedgerTotal(res.data.total || 0);
      setLedgerTotals({ fee: res.data.total_fee_amount || 0, ctc: res.data.total_signed_ctc || 0 });
    } catch (err) {
      setLedger([]);
      setLedgerTotal(0);
    } finally {
      setLedgerLoading(false);
    }
  };

  const refreshMoney = () => {
    loadStats();
    loadLedger(ledgerPage);
    loadPlacementsFor(hiredCandidates);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setPage(1);
    loadHiredCandidates(filters);
  };

  const handleResetFilters = () => {
    const resetFilters = { search: '', company: '', date_from: '', date_to: '' };
    setFilters(resetFilters);
    setPage(1);
    loadHiredCandidates(resetFilters);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // ---- edit / record -------------------------------------------------------
  const openEdit = (p) => {
    setEditing(p);
    setRecordFor(null);
    setForm({
      signed_annual_ctc: p.signed_annual_ctc ?? '',
      fee_percentage_override: p.fee_percentage_override ?? '',
      joining_date: String(p.joining_date || '').slice(0, 10),
      invoice_number: p.invoice_number || '',
      invoice_raised_at: String(p.invoice_raised_at || '').slice(0, 10),
      invoice_paid_at: String(p.invoice_paid_at || '').slice(0, 10),
      notes: p.notes || ''
    });
    setEditOpen(true);
  };

  const openRecord = (hired) => {
    setEditing(null);
    setRecordFor(hired);
    setForm({
      signed_annual_ctc: '',
      fee_percentage_override: '',
      joining_date: String(hired.hired_at || todayISO()).slice(0, 10),
      invoice_number: '',
      invoice_raised_at: '',
      invoice_paid_at: '',
      notes: hired.hired_company ? `Hired at ${hired.hired_company}` : ''
    });
    setEditOpen(true);
  };

  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const saveEdit = async () => {
    const ctc = num(form.signed_annual_ctc);
    if (!editing && !(ctc > 0)) {
      toast.error('Signed annual CTC (Rs) is required to record a placement');
      return;
    }
    if (ctc !== null && !(ctc > 0)) {
      toast.error('Signed annual CTC must be a positive number in rupees (e.g. 1200000 for 12 LPA)');
      return;
    }
    setSavingEdit(true);
    try {
      if (editing) {
        const body = {
          joining_date: form.joining_date || undefined,
          invoice_number: form.invoice_number,
          invoice_raised_at: form.invoice_raised_at,
          invoice_paid_at: form.invoice_paid_at,
          notes: form.notes
        };
        if (ctc !== null && ctc !== editing.signed_annual_ctc) body.signed_annual_ctc = ctc;
        const ov = num(form.fee_percentage_override);
        if ((ov ?? null) !== (editing.fee_percentage_override ?? null)) body.fee_percentage_override = ov ?? 0;
        const res = await api.put(`/placements/${editing.id}`, body);
        const p = res.data.placement;
        toast.success(`Saved - fee ${formatINR(p.fee_amount)} (${p.fee_basis})`);
      } else {
        const body = {
          candidate_id: recordFor.candidate_id,
          candidate_name: recordFor.candidate_name,
          job_id: recordFor.job_id || undefined,
          signed_annual_ctc: ctc,
          joining_date: form.joining_date || undefined,
          fee_percentage_override: num(form.fee_percentage_override) || undefined,
          notes: form.notes || undefined,
          source: 'hr_pipeline'
        };
        const res = await api.post('/placements', body);
        const p = res.data.placement;
        toast.success(`Recorded - fee ${formatINR(p.fee_amount)} (${p.fee_basis})`);
      }
      setEditOpen(false);
      refreshMoney();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save placement');
    } finally {
      setSavingEdit(false);
    }
  };

  const markPaid = async (p) => {
    try {
      await api.put(`/placements/${p.id}`, { invoice_paid_at: todayISO(), invoice_amount: p.fee_amount });
      toast.success(`${p.candidate_name || 'Placement'} marked paid`);
      refreshMoney();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark paid');
    }
  };

  return (
    <div className="space-y-6">
      <Header
        title="Hired Candidates"
        subtitle="Track all successfully placed candidates - and the fee each one earned"
        icon={Trophy}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Hired</p>
                  <p className="text-3xl font-bold text-green-700">{stats.total_hired}</p>
                </div>
                <Trophy className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">This Month</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.hired_this_month}</p>
                </div>
                <Calendar className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">This Week</p>
                  <p className="text-3xl font-bold text-purple-700">{stats.hired_this_week}</p>
                </div>
                <Calendar className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4">
              <div>
                <p className="text-sm text-amber-600 font-medium mb-2">Top Companies</p>
                <div className="space-y-1">
                  {stats.by_company?.slice(0, 3).map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-amber-700 truncate">{c.company}</span>
                      <Badge className="bg-amber-100 text-amber-700">{c.count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Money - every number below is a recorded placement, not an estimate */}
      {money && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="placements-money-strip">
          <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Fee Earned This Month</p>
                  <p className="text-2xl font-bold">{formatLakh(money.fee_amount_this_month)}</p>
                  <p className="text-xs text-emerald-100">{money.placements_this_month} placed · {formatLakh(money.total_fee_amount)} all time</p>
                </div>
                <IndianRupee className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className={money.invoice_overdue_count > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Invoices Overdue</p>
                  <p className="text-2xl font-bold text-red-700">{money.invoice_overdue_count || 0}</p>
                  <p className="text-xs text-slate-500">{formatLakh(money.invoice_overdue_amount)} past due</p>
                </div>
                <Receipt className="w-10 h-10 text-red-300" />
              </div>
            </CardContent>
          </Card>
          <Card className={money.invoice_due_soon_count > 0 ? 'border-amber-200 bg-amber-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-600">Due Within 7 Days</p>
                  <p className="text-2xl font-bold text-amber-700">{money.invoice_due_soon_count || 0}</p>
                  <p className="text-xs text-slate-500">{formatLakh(money.invoice_due_soon_amount)} · unpaid total {formatLakh(money.invoice_unpaid_amount)}</p>
                </div>
                <Calendar className="w-10 h-10 text-amber-300" />
              </div>
            </CardContent>
          </Card>
          <Card className={money.unconfirmed_ctc_count > 0 ? 'border-amber-200' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Provisional CTC</p>
                  <p className="text-2xl font-bold text-slate-800">{money.unconfirmed_ctc_count || 0}</p>
                  <p className="text-xs text-slate-500">recorded from expected CTC - confirm the signed figure</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-amber-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by candidate name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-9 h-9"
                data-testid="search-hired-input"
              />
            </div>

            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="h-9"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>

            <Button size="sm" onClick={handleApplyFilters} className="h-9 bg-green-600 hover:bg-green-700">
              Apply
            </Button>

            {(filters.search || filters.company || filters.date_from || filters.date_to) && (
              <Button variant="ghost" size="sm" onClick={handleResetFilters} className="h-9">
                Reset
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Hired At Company
                </label>
                <Select value={filters.company || 'all'} onValueChange={(v) => handleFilterChange('company', v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {filterOptions.companies?.map(company => (
                      <SelectItem key={company} value={company}>{company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Hired From
                </label>
                <DatePicker
                  value={filters.date_from}
                  onChange={(date) => handleFilterChange('date_from', date)}
                  placeholder="Select date"
                  className="h-9"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Hired To
                </label>
                <DatePicker
                  value={filters.date_to}
                  onChange={(date) => handleFilterChange('date_to', date)}
                  placeholder="Select date"
                  className="h-9"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hired Candidates List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-green-500" />
          </div>
        ) : hiredCandidates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Trophy className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No hired candidates found</p>
              <p className="text-sm mt-1">Candidates marked as hired will appear here</p>
            </CardContent>
          </Card>
        ) : (
          hiredCandidates.map((candidate) => {
            const isExpanded = expandedId === candidate.share_id;
            const placement = placementsByCandidate[candidate.candidate_id];

            return (
              <Card
                key={candidate.share_id}
                className="hover:shadow-md transition-shadow border-l-4 border-l-green-500"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-lg shrink-0">
                        {candidate.candidate_name?.[0] || 'C'}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-800 text-lg">
                            {candidate.candidate_name}
                          </h3>
                          <Badge className="bg-green-100 text-green-700">
                            <Trophy className="w-3 h-3 mr-1" />
                            Hired
                          </Badge>
                        </div>

                        <p className="text-sm text-slate-600 mt-1">
                          {candidate.candidate_designation || 'Position not specified'}
                        </p>

                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 text-green-500" />
                            <strong className="text-green-700">{candidate.hired_company}</strong>
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(candidate.hired_at)}
                          </span>
                          {candidate.job_title && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {candidate.job_title}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedId(isExpanded ? null : candidate.share_id)}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>

                  <PlacementLine
                    p={placement}
                    onEdit={() => openEdit(placement)}
                    onRecord={() => openRecord(candidate)}
                  />

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-700">Contact Information</h4>
                        {candidate.candidate_email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4 text-slate-400" />
                            {candidate.candidate_email}
                          </div>
                        )}
                        {candidate.candidate_phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4 text-slate-400" />
                            {candidate.candidate_phone}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-700">Hiring Details</h4>
                        <div className="text-sm text-slate-600">
                          <p><strong>Original Position:</strong> {candidate.job_title}</p>
                          {candidate.original_company && (
                            <p><strong>Original Company:</strong> {candidate.original_company}</p>
                          )}
                          {candidate.hr_name && (
                            <p><strong>HR Contact:</strong> {candidate.hr_name} ({candidate.hr_email})</p>
                          )}
                        </div>
                        {candidate.hired_notes && (
                          <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-slate-600">
                            <strong>Notes:</strong> {candidate.hired_notes}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}

        {total > itemsPerPage && (
          <div className="flex justify-center mt-4">
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(total / itemsPerPage)}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Placements ledger - every recorded placement, from any path (pipeline Joined, Mark as Hired, phone) */}
      <Card data-testid="placements-ledger">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" />
              Placements ledger
              <span className="text-xs font-normal text-slate-500">
                {ledgerTotal} rows · fee {formatLakh(ledgerTotals.fee)} · CTC {formatLakh(ledgerTotals.ctc)}
              </span>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Candidate or client..."
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setLedgerPage(1); loadLedger(1); } }}
                  className="pl-9 h-8 w-[200px]"
                  data-testid="ledger-search"
                />
              </div>
              <Button size="sm" variant={ledgerUnpaid ? 'default' : 'outline'} className="h-8"
                onClick={() => { setLedgerPage(1); setLedgerUnpaid(!ledgerUnpaid); }}>
                Unpaid only
              </Button>
              <Button size="sm" variant={ledgerUnconfirmed ? 'default' : 'outline'} className="h-8"
                onClick={() => { setLedgerPage(1); setLedgerUnconfirmed(!ledgerUnconfirmed); }}>
                Provisional only
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => { setLedgerPage(1); loadLedger(1); }}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {ledgerLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
          ) : ledger.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              No placements recorded yet. Moving a pipeline candidate to Joined, or Mark as Hired with a signed CTC, writes one here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-slate-50 border-y">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Candidate</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Client</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Joined</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Signed CTC</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Fee</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Basis</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Invoice</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ledger.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50" data-testid={`ledger-row-${p.id}`}>
                      <td className="px-3 py-2">
                        <Link to={`/ats/candidates/${p.candidate_id}`} className="font-medium text-slate-800 hover:text-emerald-700">
                          {p.candidate_name || p.candidate_id}
                        </Link>
                        <div className="flex items-center gap-1 mt-0.5">
                          <CtcBadge p={p} />
                          <span className="text-[10px] text-slate-400">{p.source}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {p.client_name || <span className="text-slate-400">no client</span>}
                        {p.requirement_id && (
                          <Link to={`/ats/requirements/${p.requirement_id}`} className="block text-xs text-blue-600 hover:underline">requirement</Link>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{String(p.joining_date || '').slice(0, 10) || '-'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{formatINR(p.signed_annual_ctc)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-medium text-emerald-700">
                        {formatINR(p.fee_amount)}
                        {p.fee_percentage ? <span className="block text-[10px] text-slate-400 font-normal">{p.fee_percentage}%</span> : null}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 max-w-[180px] truncate" title={p.fee_basis}>{p.fee_basis}</td>
                      <td className="px-3 py-2">
                        <InvoiceBadge p={p} />
                        {p.invoice_number && <span className="block text-[10px] text-slate-400 mt-0.5">{p.invoice_number}</span>}
                        <span className="block text-[10px] text-slate-400">{p.payment_due_days}d terms</span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEdit(p)} data-testid={`ledger-edit-${p.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {!p.invoice_paid && (
                          <Button size="sm" variant="outline" className="h-7 px-2 ml-1" onClick={() => markPaid(p)} data-testid={`ledger-paid-${p.id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {ledgerTotal > ledgerPerPage && (
            <div className="p-2 flex items-center justify-between text-xs text-slate-500 border-t">
              <Button variant="ghost" size="sm" disabled={ledgerPage <= 1} onClick={() => setLedgerPage(ledgerPage - 1)}>Prev</Button>
              <span>Page {ledgerPage} of {Math.max(1, Math.ceil(ledgerTotal / ledgerPerPage))}</span>
              <Button variant="ghost" size="sm" disabled={ledgerPage * ledgerPerPage >= ledgerTotal} onClick={() => setLedgerPage(ledgerPage + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit / record placement */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="placement-edit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-600" />
              {editing ? `Edit placement - ${editing.candidate_name || ''}` : `Record placement - ${recordFor?.candidate_name || ''}`}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? `The fee is recomputed by the fee engine from the signed CTC (${editing.fee_basis}).`
                : 'Type the signed annual CTC in rupees. The fee comes from the client rate on file, or the DCS rate card.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Signed annual CTC (Rs) {editing ? '' : '*'}</Label>
                <Input type="number" min="1" step="1" placeholder="e.g. 1200000"
                  value={form.signed_annual_ctc}
                  onChange={(e) => setForm({ ...form, signed_annual_ctc: e.target.value })}
                  data-testid="placement-ctc-input" />
                {form.signed_annual_ctc > 0 && form.signed_annual_ctc < 1000 && (
                  <p className="text-xs text-amber-600 mt-1">That looks like lakhs - enter the full rupee figure (12 LPA = 1200000).</p>
                )}
              </div>
              <div>
                <Label>Fee override (%)</Label>
                <Input type="number" min="0" max="100" step="0.01" placeholder="blank = client rate / rate card"
                  value={form.fee_percentage_override}
                  onChange={(e) => setForm({ ...form, fee_percentage_override: e.target.value })}
                  data-testid="placement-override-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Joining date</Label>
                <Input type="date" value={form.joining_date}
                  onChange={(e) => setForm({ ...form, joining_date: e.target.value })}
                  data-testid="placement-joining-input" />
              </div>
              <div>
                <Label>Invoice number</Label>
                <Input value={form.invoice_number} placeholder="optional"
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  disabled={!editing} />
              </div>
            </div>
            {editing && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Invoice raised on</Label>
                  <Input type="date" value={form.invoice_raised_at}
                    onChange={(e) => setForm({ ...form, invoice_raised_at: e.target.value })} />
                </div>
                <div>
                  <Label>Invoice paid on</Label>
                  <Input type="date" value={form.invoice_paid_at}
                    onChange={(e) => setForm({ ...form, invoice_paid_at: e.target.value })}
                    data-testid="placement-paid-input" />
                </div>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {editing && (
              <p className="text-xs text-slate-500">
                Current: CTC {formatINR(editing.signed_annual_ctc)} · fee {formatINR(editing.fee_amount)} · {editing.fee_basis} · terms {editing.payment_due_days}d
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="placement-save-btn">
              {savingEdit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Save' : 'Record placement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
